import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Listbox, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { CommunicationType, displayPhoneNumber } from "shared";
import { useParams } from "react-router";

import { useCommunications } from "../../../../../hooks/useCommunications";
import { useInboundEmailAttachmentDownload } from "../../../../../hooks/useInboundEmailAttachmentDownload";
import { LoadingOverlay } from "../../../../Molecules/LoadingOverlay";
import { Heading } from "../../../../../../common/Atoms/Typography/Heading";
import { Paragraph } from "../../../../../../common/Atoms/Typography/Paragraph";
import { HelpIcon } from "../../../../Molecules/HelpIcon";
import { useContact } from "../../../../../hooks/useContact";
import { useRemoveActionMutation } from "../../../../../services/api/actionApi/action";
import { SmsReplyComposer } from "../../../../Molecules/SmsReplyComposer";
import { EmailReplyComposer } from "../../../../Molecules/EmailReplyComposer";

import { EmailCommunication } from "./EmailCommunication";
import { InboundEmailCommunication } from "./InboundEmailCommunication";
import { SmsCommunication } from "./SmsCommunication";

function extractEmailAddress(value?: string) {
  if (!value) {
    return undefined;
  }

  const angleBracketMatch = value.match(/<([^>]+)>/);

  return (angleBracketMatch?.[1] || value).trim() || undefined;
}

function parseEmailAddressList(value?: string): string[] {
  if (!value) {
    return [];
  }

  return value
    .split(`,`)
    .map(part => extractEmailAddress(part))
    .filter((email): email is string => !!email);
}

function buildDefaultContactRecipientEmails(contact?: ReturnType<typeof useContact>[`data`]) {
  if (!contact) {
    return [];
  }

  const recipientEmails = [
    ...(contact.contactPersons || [])
      .filter(contactPerson => contactPerson.includeInNotifications)
      .map(contactPerson => extractEmailAddress(contactPerson.email))
      .filter((email): email is string => !!email),
    extractEmailAddress(contact.primaryContactPerson?.email),
  ].filter((email): email is string => !!email);

  return [...new Set(recipientEmails)];
}

const replyModeOptions: Array<{ value: `sms` | `email`; label: string }> = [
  { value: `email`, label: `Email` },
  { value: `sms`, label: `SMS` },
];

export function ContactCommunications() {
  const { contactId, organisationId } = useParams<{ contactId?: string; organisationId?: string }>();
  const { data, isLoading, isFetching, refetch } = useCommunications();
  const { downloadAttachment, downloading } = useInboundEmailAttachmentDownload();
  const { data: contact } = useContact();
  const [replyMode, setReplyMode] = useState<`sms` | `email`>(`email`);
  const [inlineReplyInboundEmailId, setInlineReplyInboundEmailId] = useState<string | null>(null);
  const [removeAction] = useRemoveActionMutation();
  const [highlightedEmailMessageId, setHighlightedEmailMessageId] = useState<number | null>(null);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const communicationsContainerRef = useRef<HTMLDivElement>(null);
  const communications = data?.communications || [];
  const destinationNumber = useMemo(() => {
    const latestSms = [...communications]
      .reverse()
      .find(comm => comm.type === CommunicationType.SMS && !!comm.sms);

    if (!latestSms?.sms) {
      return undefined;
    }

    if (latestSms.sms.direction === `inbound`) {
      return latestSms.sms.from;
    }

    return latestSms.sms.to;
  }, [communications]);
  const parsedContactId = contactId ? Number(contactId) : undefined;
  const primaryMobileNumber = displayPhoneNumber(contact?.primaryContactPerson?.mobileCountryCode, contact?.primaryContactPerson?.mobileNumber);
  const primaryDefaultNumber = displayPhoneNumber(contact?.primaryContactPerson?.defaultCountryCode, contact?.primaryContactPerson?.defaultNumber);
  const hasPrimaryContactPhoneNumber = !!(primaryMobileNumber || primaryDefaultNumber);
  const hasConversationDestinationNumber = !!destinationNumber;
  const hasSmsDestination = hasPrimaryContactPhoneNumber || hasConversationDestinationNumber;
  const sendSmsDisabledReason = hasSmsDestination ? `` : `No phone number available to send SMS to.`;
  const defaultEmailRecipients = buildDefaultContactRecipientEmails(contact);
  const loadedOutboundEmailMessageIds = useMemo(() => {
    return new Set(
      communications
        .filter(comm => comm.type === CommunicationType.EMAIL && !!comm.email?.sendgridMessageInternalId)
        .map(comm => comm.email?.sendgridMessageInternalId)
        .filter((id): id is number => typeof id === `number`),
    );
  }, [communications]);
  const selectedReplyModeOption = replyModeOptions.find(option => option.value === replyMode) || replyModeOptions[0];

  const scrollToEmailMessage = useCallback((sendgridMessageInternalId: number) => {
    const target = document.getElementById(`outbound-email-${sendgridMessageInternalId}`);

    if (!target) {
      return;
    }

    target.scrollIntoView({
      behavior: `smooth`,
      block: `center`,
    });

    setHighlightedEmailMessageId(sendgridMessageInternalId);

    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }

    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedEmailMessageId(null);
    }, 2200);
  }, []);

  useEffect(() => {
    if (!communicationsContainerRef.current) return;
    communicationsContainerRef.current.scrollTop = communicationsContainerRef.current.scrollHeight;
  }, [communications.length]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!inlineReplyInboundEmailId) {
      return;
    }

    const timeoutId = setTimeout(() => {
      const inlineReplyElement = document.getElementById(`inbound-email-inline-reply-${inlineReplyInboundEmailId}`);

      if (!inlineReplyElement) {
        return;
      }

      inlineReplyElement.scrollIntoView({
        behavior: `smooth`,
        block: `center`,
      });
    }, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [inlineReplyInboundEmailId]);

  const onDeleteFailedOrSuspendedSms = async(actionId: string) => {
    if (!organisationId) {
      return;
    }

    try {
      await removeAction({
        organisationId,
        actionId,
      }).unwrap();

      await refetch();
    }
    catch {
      // Keep existing communications visible when delete fails.
    }
  };

  return (
    <LoadingOverlay
      loading={ isLoading || isFetching }
    >
      <div className={ `px-4 pt-3 pb-2 border-b border-gray-200 bg-white` }>
        <div className={ `flex items-center space-x-2` }>
          <Heading size={ `lg` }
            className={ `tracking-tight` }>
            { `Recent communications` }
          </Heading>
          <HelpIcon tooltip={ `Inbound email attachments can be downloaded when available. Historical SMS replies may not appear here.` } />
        </div>
        <Paragraph size={ `sm` }
          variant={ `secondary` }
          className={ `mt-1` }>
          { `Monitor outbound emails, inbound email replies, and SMS messages for this contact.` }
        </Paragraph>
      </div>
      <div className={ `px-4 pb-4` }>
        <div
          ref={ communicationsContainerRef }
          className={ `max-h-[70vh] overflow-y-auto space-y-6 pr-2` }>
          { communications.length === 0 && (
            <div className={ `text-sm text-gray-500` }>{ `No communications to show yet.` }</div>
          ) }

          { communications.map((comm, idx) => {
            if (comm.type === CommunicationType.EMAIL && comm.email) {
              return <EmailCommunication
                email={ comm.email }
                messageDomId={ `outbound-email-${comm.email.sendgridMessageInternalId}` }
                isHighlighted={ highlightedEmailMessageId === comm.email.sendgridMessageInternalId }
                key={ `${idx}-${comm.email.sendgridMessageInternalId}` }
              />;
            }

            if (comm.type === CommunicationType.INBOUND_EMAIL && comm.inboundEmail) {
              const inboundEmail = comm.inboundEmail;
              const matchedSendgridMessageInternalId = comm.inboundEmail.matchedSendgridMessageInternalId;
              const canShowOriginalMessageLink = typeof matchedSendgridMessageInternalId === `number`
                && loadedOutboundEmailMessageIds.has(matchedSendgridMessageInternalId);
              const replyAllRecipients = [...new Set([
                ...parseEmailAddressList(comm.inboundEmail.forwardedFrom || comm.inboundEmail.from),
                ...parseEmailAddressList(comm.inboundEmail.cc),
              ])];
              const defaultReplySubject = comm.inboundEmail.subject?.toLowerCase().startsWith(`re:`)
                ? comm.inboundEmail.subject
                : comm.inboundEmail.subject
                  ? `Re: ${comm.inboundEmail.subject}`
                  : `Re:`;
              const isInlineReplyOpen = inlineReplyInboundEmailId === comm.inboundEmail.id;

              return (
                <div
                  className={ `space-y-2` }
                  key={ `${idx}-${comm.inboundEmail.id}` }
                >
                  <InboundEmailCommunication
                    inboundEmail={ inboundEmail }
                    showOriginalMessageLink={ canShowOriginalMessageLink }
                    onViewOriginalMessage={
                      canShowOriginalMessageLink && typeof matchedSendgridMessageInternalId === `number`
                        ? () => scrollToEmailMessage(matchedSendgridMessageInternalId)
                        : undefined
                    }
                    isReplyOpen={ isInlineReplyOpen }
                    onReplyByEmail={ () => {
                      setInlineReplyInboundEmailId(currentId => currentId === inboundEmail.id ? null : inboundEmail.id);
                    } }
                    onDownloadAttachment={ (attachmentIndex, attachmentName) => {
                      void downloadAttachment(inboundEmail.id, attachmentIndex, attachmentName);
                    } }
                    downloadingAttachmentIndex={
                      downloading?.inboundEmailId === inboundEmail.id
                        ? downloading.index
                        : null
                    }
                  />
                  { isInlineReplyOpen ? (
                    <div
                      id={ `inbound-email-inline-reply-${inboundEmail.id}` }
                      className={ `max-w-3xl` }>
                      <EmailReplyComposer
                        organisationId={ organisationId }
                        contactId={ parsedContactId }
                        defaultRecipients={ replyAllRecipients }
                        defaultSubject={ defaultReplySubject }
                        replyToInboundEmailId={ inboundEmail.id }
                        titleLabel={ `Reply to this email` }
                        bodyPlaceholder={ `Type your reply...` }
                        showSubjectInput={ false }
                        containerClassName={ `mt-2 border-l-2 border-teal-200 pl-3 pt-0` }
                        onSent={ async() => {
                          setInlineReplyInboundEmailId(null);
                          await refetch();
                        } }
                      />
                    </div>
                  ) : null }
                </div>
              );
            }

            if (comm.type === CommunicationType.SMS && comm.sms) {
              return <SmsCommunication
                sms={ comm.sms }
                onDeleteFailedOrSuspended={ onDeleteFailedOrSuspendedSms }
                key={ `${idx}-${comm.sms.id}` }
              />;
            }

            return null;
          }) }
        </div>
        <div className={ `mt-4 flex items-center gap-2` }>
          <span className={ `text-sm font-medium text-gray-700` }>{ `Reply via` }</span>
          <Listbox value={ replyMode }
            onChange={ setReplyMode }>
            { ({ open }) => (
              <div className={ `relative` }>
                <Listbox.Button className={ `inline-flex cursor-pointer items-center gap-1 rounded-full border border-indigo-300 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-700 transition-colors duration-200 hover:bg-indigo-50 focus:outline-hidden focus:ring-2 focus:ring-indigo-200` }>
                  <span>{ selectedReplyModeOption.label }</span>
                  <ChevronDownIcon className={ `h-4 w-4 text-indigo-500 transition-transform duration-200 ${open ? `rotate-180` : ``}` } />
                </Listbox.Button>
                <Transition
                  as={ Fragment }
                  leave={ `transition ease-in duration-100` }
                  leaveFrom={ `opacity-100` }
                  leaveTo={ `opacity-0` }>
                  <Listbox.Options className={ `absolute z-20 mt-2 min-w-[120px] rounded-xl border border-gray-200 bg-white p-1 shadow-lg` }>
                    { replyModeOptions.map(option => (
                      <Listbox.Option
                        key={ option.value }
                        value={ option.value }>
                        { ({ active, selected }) => (
                          <div className={ `cursor-pointer rounded-lg px-3 py-2 text-sm font-medium ${active ? `bg-indigo-50 text-indigo-700` : `text-gray-700`} ${selected ? `text-indigo-700` : ``}` }>
                            { option.label }
                          </div>
                        ) }
                      </Listbox.Option>
                    )) }
                  </Listbox.Options>
                </Transition>
              </div>
            ) }
          </Listbox>
        </div>
        { replyMode === `email` ? (
          <EmailReplyComposer
            organisationId={ organisationId }
            contactId={ parsedContactId }
            defaultRecipients={ defaultEmailRecipients }
            defaultSubject={ `` }
            titleLabel={ `Send new email` }
            bodyPlaceholder={ `Type your email...` }
            onSent={ refetch }
          />
        ) : (
          <SmsReplyComposer
            organisationId={ organisationId }
            contactId={ parsedContactId }
            destinationNumber={ destinationNumber }
            hasSmsDestination={ hasSmsDestination }
            sendSmsDisabledReason={ sendSmsDisabledReason }
            onSent={ refetch }
          />
        ) }
      </div>
    </LoadingOverlay>
  );
}
